/**
 *==================================================
 * Licensed Materials - Property of HCL Technologies
 *
 * HCL Commerce
 *
 * (C) Copyright HCL Technologies Limited 2020
 *
 *==================================================
 */
//Standard libraries
import React from "react";
import { useTranslation } from "react-i18next";
import { LazyComponentProps, trackWindowScroll } from "react-lazy-load-image-component";
//Foundation libraries
import { useProductGridLayout } from "./hooks/use-product-grid-layout";
//Custom libraries
import {
  CatalogEntryListWidget as CatalogEntryList,
  QuickInfoProvider,
  QuickInfoContext,
} from "@hcl-commerce-store-sdk/react-component";
import { SEARCH } from "../../../constants/routes";
import useProductCardWidget from "../../widgets/product-card/ProductCardWidget";
import FormattedPriceDisplay from "../../widgets/formatted-price-display";
import { useCompare } from "../../../_foundation/hooks/use-compare";
import { useLocation } from "react-router";
import { useEffect } from "react";
import { CompareCollectorWidget } from "../../widgets/compare/collector";
import { Link } from "react-router-dom";

//remove it
import Grid from "@material-ui/core/Grid";
import { Button } from "@material-ui/core";
import { StyledButton } from "@hcl-commerce-store-sdk/react-component";
import { QuickViewModal } from "../../quick-view-modal";
interface ProductGridProps extends LazyComponentProps {
  cid: string;
  page?: any;
  searchTerm?: string;
  tagTerm?: string;
}
/**
 * Product Grid component
 * displays catalog entry listing, pagination and selected facets
 * @summary Displays Product Card List in Grid Layout.
 * `@prop {any} props` have following properties:
 * `@property {String} cid(required)` Unique identifier used for layouts.`
 * `@property {String} page(optional)`  page object has category info such as category identifier.`
 * `@property {String} searchTerm(optional)` The input string for searching.`
 */
const CatalogEntryListWidget: React.FC<ProductGridProps> = (props: any) => {
  const cid = props.cid;
  const categoryId: string = props.page?.tokenValue ?? "";
  const searchTerm: string = props.searchTerm ?? "";
  const pageName: string = props.page?.name ?? "";
  const categoryName: string = props.page?.externalContext?.identifier ?? "";
  const primaryBannerRefresh = props.primaryBannerRefresh;
  const secondaryBannerRefresh = props.secondaryBannerRefresh;
  const footerBannerRefresh = props.footerBannerRefresh;

  const location: any = useLocation();
  const { compare: currentCompare } = location?.state ?? {};
  const { compare } = useCompare();
  const queryString = location.search;
  const params = new URLSearchParams(queryString);
  const tagTerm = params.get("tag") ?? "";
  const page = "CatalogEntryList";

  const {
    isValidUrl,
    onPageChange,
    clearPriceFacet,
    onSortOptionChange,
    onClearAll,
    onFacetRemove,
    priceSelected,
    selectedFacets,
    productListTotal,
    selectedSortOption,
    pageLimit,
    selectedPageOffset,
    defaultCurrencyID,
    selectedMaxPrice,
    productList,
    selectedMinPrice,
    sortOptions,
    suggestedKeywords,
    selectFacetRemove,
    ssPriceFacetOptions,
    searchSpringMerchandising,
  } = useProductGridLayout({ categoryId, searchTerm, CatalogEntryListWidget, pageName, categoryName, tagTerm });

  const bannerContent = searchSpringMerchandising.content ?? "";
  const ProductCards = useProductCardWidget({
    productListTotal,
    productList,
    categoryId,
    compare,
    selectedFacets,
    priceSelected,
    pageName,
    categoryName,
    searchTerm,
    page,
    bannerContent,
  });

  useEffect(() => {
    if (primaryBannerRefresh && secondaryBannerRefresh && footerBannerRefresh) {
      let headerPrimaryBanner: any;
      let headerSecondaryBanner: any;
      let hasBanner = false;

      if (searchSpringMerchandising && searchSpringMerchandising.content && searchSpringMerchandising.content.header) {
        Object.entries(searchSpringMerchandising.content.header).map(([key, value]: any) => {
          hasBanner = true;
          headerPrimaryBanner = value;
          primaryBannerRefresh(headerPrimaryBanner);
        });
      }

      if (searchSpringMerchandising && searchSpringMerchandising.content && searchSpringMerchandising.content.banner) {
        Object.entries(searchSpringMerchandising.content.banner).map(([key, value]: any) => {
          hasBanner = true;
          headerSecondaryBanner = value;
          secondaryBannerRefresh(headerSecondaryBanner);
        });
      }

      if (searchSpringMerchandising && searchSpringMerchandising.content && searchSpringMerchandising.content.footer) {
        Object.entries(searchSpringMerchandising.content.footer).map(([key, value]: any) => {
          hasBanner = true;
          headerSecondaryBanner = value;
          footerBannerRefresh(headerSecondaryBanner);
        });
      }
    }
  }, [bannerContent]);

  const formattedPriceDisplay = (
    <FormattedPriceDisplay min={selectedMinPrice} max={selectedMaxPrice} currency={defaultCurrencyID} />
  );
  const { t } = useTranslation();

  // Translation File values
  const translation = {
    ProductGridLabelsnoMatches: t("ProductGrid.Labels.noMatches", {
      searchTerm: searchTerm,
    }),
    ProductGridLabelssearchAgain: t("ProductGrid.Labels.searchAgain", {
      searchTerm: searchTerm,
    }),
    ProductGridLabelssuggestion: t("ProductGrid.Labels.suggestion"),
    ProductGridLabelsproductFound: t("ProductGrid.Labels.productFound", {
      count: productListTotal,
    }),
    ProductGridLabelsproductSearchFound: t("ProductGrid.Labels.productSearchFound", {
      count: productListTotal,
      searchTerm: searchTerm,
    }),
    ProductGridLabelsfilteredBy: t("ProductGrid.Labels.filteredBy"),
    ProductGridActionsclearAll: t("ProductGrid.Actions.clearAll"),
    optiontranslationKey: sortOptions?.map((option: any) => t(`${option.translationKey}`)),
  };

  useEffect(() => {
    compare?.copyFrom(currentCompare);
  }, [currentCompare]); // eslint-disable-line react-hooks/exhaustive-deps
  //
  function Blog(props) {
    const content = props.posts.map((post) => (
      <div key={post.id}>
        <h3>{post.title}</h3>
        <p>{post.content}</p>
      </div>
    ));
    return <div>{content}</div>;
  }

  const posts = [
    //{id: 1, title: 'Hello World', content: 'Welcome to learning React!'},
    //{id: 2, title: 'Installation', content: 'You can install React from npm.'}
  ];
  //

  return (
    <QuickInfoProvider>
      <div
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          height: "100%",
        }}>
        <Blog posts={posts} />

        <span style={{ flex: "1" }}>
          <QuickViewModal />

          <CatalogEntryList
            {...{
              cid,
              categoryId,
              searchTerm,
              isValidUrl,
              onPageChange,
              clearPriceFacet,
              onSortOptionChange,
              onClearAll,
              onFacetRemove,
              priceSelected,
              selectedFacets,
              productListTotal,
              selectedSortOption,
              pageLimit,
              productList,
              sortOptions,
              selectedPageOffset,
              suggestedKeywords,
              SEARCH,
              formattedPriceDisplay,
              ProductCards,
              translation,
              selectFacetRemove,
              compare,
              categoryName,
              pageName,
              ssPriceFacetOptions,
              tagTerm,
              searchSpringMerchandising,
            }}
          />
        </span>
        {productList.length && compare?.data.len ? <CompareCollectorWidget {...{ compare }} /> : null}
      </div>
    </QuickInfoProvider>
  );
};

export default trackWindowScroll(CatalogEntryListWidget);
